// services/authService.js

class AuthService {
  constructor() {
    this.token = null;
    this.refreshToken = null;
    this.decodedToken = null;
  }

  // Store tokens after successful OTP verification
  setTokens(token, refreshToken) {
    this.token = token;
    this.refreshToken = refreshToken;

    // Decode the JWT token to extract resumeId and jobDescriptionId
    this.decodedToken = this.decodeJWT(token);

    // Optionally store in sessionStorage for persistence
    sessionStorage.setItem("authToken", token);
    sessionStorage.setItem("refreshToken", refreshToken);
    sessionStorage.setItem("interviewAuthToken", token);
    sessionStorage.setItem("interviewRefreshToken", refreshToken);

    localStorage.setItem("access_token", token);
    localStorage.setItem("refresh_token", refreshToken);
  }

  // Get current access token
  getToken() {
    if (!this.token) {
      // Try to get from sessionStorage if not in memory
      this.token =
        sessionStorage.getItem("authToken") ||
        localStorage.getItem("access_token") ||
        sessionStorage.getItem("interviewAuthToken");
    }
    return this.token;
  }

  // Get refresh token
  getRefreshToken() {
    if (!this.refreshToken) {
      // Try to get from sessionStorage if not in memory
      this.refreshToken =
        localStorage.getItem("refresh_token") ||
        sessionStorage.getItem("refreshToken") ||
        sessionStorage.getItem("interviewRefreshToken");
    }
    return this.refreshToken;
  }

  // Decode JWT token manually (without external libraries)
  decodeJWT(token) {
    try {
      const parts = token.split(".");

      if (parts.length !== 3) {
        throw new Error("Invalid JWT token format");
      }

      const payload = parts[1];
      const paddedPayload =
        payload + "=".repeat((4 - (payload.length % 4)) % 4);
      const base64 = paddedPayload.replace(/-/g, "+").replace(/_/g, "/");

      const jsonPayload =
        typeof atob === "function"
          ? atob(base64)
          : Buffer.from(base64, "base64").toString("utf-8");

      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error("Error decoding JWT token:", error);
      return null;
    }
  }

  // Get resumeId from decoded token
  getResumeId() {
    if (!this.decodedToken && this.token) {
      this.decodedToken = this.decodeJWT(this.token);
    }
    return this.decodedToken?.user_id || null;
  }
  getCandidateName() {
    if (!this.decodedToken && this.token) {
      this.decodedToken = this.decodeJWT(this.token);
    }
    return this.decodedToken?.candidate_name || null;
  }

  // Get jobDescriptionId from decoded token
  getJobDescriptionId() {
    if (!this.decodedToken && this.token) {
      this.decodedToken = this.decodeJWT(this.token);
    }
    return this.decodedToken?.job_id || null;
  }

  // Get all user info from decoded token
  getUserInfo() {
    if (!this.decodedToken && this.token) {
      this.decodedToken = this.decodeJWT(this.token);
    }

    return this.decodedToken;
  }

  // Check if token is expired
  isTokenExpired() {
    const token = this.getToken();

    if (!token) {
      return true;
    }

    if (!this.decodedToken) {
      this.decodedToken = this.decodeJWT(token);
    }

    if (!this.decodedToken || !this.decodedToken.exp) {
      console.warn("Token decoded but no expiration found");
      return true;
    }

    const currentTime = Math.floor(Date.now() / 1000);
    return this.decodedToken.exp < currentTime;
  }

  // Refresh the access token using refresh token
  async refreshAccessToken() {
    const refreshToken = this.getRefreshToken();

    if (!refreshToken) {
      throw new Error("No refresh token available");
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_ROOT_URL}/auth/api/v1/Authentication/screenup/refresh`,
        {
          method: "POST",
          headers: {
            accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            refresh_token: refreshToken,
          }),
        },
      );

      if (response.ok) {
        const data = await response.json();

        const accessToken = data.access_token || data.token;
        const newRefreshToken =
          data.refresh_token || data.refreshToken || refreshToken;

        if (!accessToken) {
          throw new Error("Refresh API did not return an access token");
        }

        this.setTokens(accessToken, newRefreshToken);

        return accessToken;
      } else {
        throw new Error("Failed to refresh token");
      }
    } catch (error) {
      console.error("Error refreshing token:", error);
      this.clearTokens();
      throw error;
    }
  }

  // Make authenticated API request with automatic token refresh
  async makeAuthenticatedRequest(url, options = {}) {
    let token = this.getToken();

    // Check if token is expired and refresh if needed
    if (this.isTokenExpired()) {
      try {
        token = await this.refreshAccessToken();
      } catch (error) {
        throw new Error("Authentication failed. Please login again.");
      }
    }

    // Add authorization header
    const headers = {
      ...options.headers,
      Authorization: `Bearer ${token}`,
      accept: "application/json",
      "Content-Type": "application/json",
    };

    const requestOptions = {
      ...options,
      headers,
    };

    try {
      const response = await fetch(url, requestOptions);

      // If we get 401, try to refresh token once
      if (response.status === 401) {
        try {
          token = await this.refreshAccessToken();
          requestOptions.headers["Authorization"] = `Bearer ${token}`;
          return await fetch(url, requestOptions);
        } catch (refreshError) {
          this.clearTokens();
          throw new Error("Authentication failed. Please login again.");
        }
      }

      return response;
    } catch (error) {
      console.error("API request failed:", error);
      throw error;
    }
  }

  // Clear all tokens (logout)
  clearTokens() {
    this.token = null;
    this.refreshToken = null;
    this.decodedToken = null;

    sessionStorage.removeItem("authToken");
    sessionStorage.removeItem("refreshToken");
  }

  // Check if user is authenticated
  isAuthenticated() {
    const token = this.getToken();

    if (!token) {
      console.warn("No token found");
      return false;
    }

    const isExpired = this.isTokenExpired();

    if (isExpired) {
      console.warn("Token is expired");
      return false;
    }

    return true;
  }

  // Initialize service (call this on app startup)
  initialize() {
    try {
      // Try to load tokens from sessionStorage
      const token = sessionStorage.getItem("authToken");
      const refreshToken = sessionStorage.getItem("refreshToken");

      if (token && refreshToken) {
        this.token = token;
        this.refreshToken = refreshToken;
        this.decodedToken = this.decodeJWT(token);

        if (this.decodedToken) {
        } else {
          this.clearTokens();
        }
      } else {
      }
    } catch (error) {
      console.error("Error initializing AuthService:", error);
      this.clearTokens();
    }
  }
}

// Create singleton instance
const authService = new AuthService();

export default authService;
